
import { defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// constants
const COMPANIES_TYPES = [
    { id: 1, name: 'ТОО' },
    { id: 2, name: 'ИП' },
    { id: 3, name: 'АО' },
]

const BUSINESS_MODELS = [
    { id: 1, name: 'B2B' },
    { id: 2, name: 'B2C' },
]

const COUNTRIES = [
    { id: 1, name: 'Республика Казахстан' },
    { id: 2, name: 'Россия' },
]

// realisations
import { deliveryCompanies } from '@/core/realisations/delivery-companies.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { required, integer, email } from '@vuelidate/validators'
import { incorrectSymbolValidator, binValidator } from '@/common/validators/common.validators'

// composable
import useNotifications from '@/common/composable/useNotifications'

// types
import { DeliveryCompanyPayload } from '@/core/types/delivery-companies.types'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheInput,
        TheSelect,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const notifications = useNotifications()

        const company_id = Number(route.params.company_id as string)

        const state = reactive({
            is_loading: false,
            is_company_loading: false,
            //
            name: null as string | null,
            shortName: null as string | null,
            businessModelId: null as number | null,
            bin: null as string | null,
            contactPhone: null as string | null,
            email: null as string | null,
            kbe: null as string | null,
            legalAddress: null as string | null,
            actualAddress: null as string | null,
            bank: null as string | null,
            bik: null as string | null,
            iik: null as string | null,
            vatCertificateNumber: null as string | null,
            vatSeries: null as string | null,
            vatCertificateDate: null as string | null,
            countryId: null as number | null,
            typeId: null as number | null,
        })

        const state_form$ = useVuelidate(
            {
                name: { required, incorrectSymbolValidator },
                businessModelId: { required },
                bin: { required, integer, binValidator },
                contactPhone: { required, incorrectSymbolValidator },
                email: { required, email },
                kbe: { required, integer },
                legalAddress: { required, incorrectSymbolValidator },
                actualAddress: { required, incorrectSymbolValidator },
                bank: { required, incorrectSymbolValidator },
                bik: { required, incorrectSymbolValidator },
                iik: { required, incorrectSymbolValidator },
                vatCertificateNumber: { required },
                vatSeries: { required },
                vatCertificateDate: { required },
            },
            state
        )

        const loadCompany = async () => {
            try {
                state.is_company_loading = true

                const response = await deliveryCompanies.getCompany(company_id)
                state.name = response.data.name
                state.shortName = response.data.shortName
                state.businessModelId = response.data.businessModelId
                state.bin = response.data.bin
                state.contactPhone = response.data.contactPhone
                state.email = response.data.email
                state.kbe = response.data.kbe
                state.legalAddress = response.data.legalAddress
                state.actualAddress = response.data.actualAddress
                state.bank = response.data.bank
                state.bik = response.data.bik
                state.iik = response.data.iik
                state.vatCertificateNumber = response.data.vatCertificateNumber
                state.vatSeries = response.data.vatSeries
                state.vatCertificateDate = response.data.vatCertificateDate
                state.countryId = response.data.countryId
                state.typeId = response.data.typeId
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных компании произошла ошибка',
                })
            } finally {
                state.is_company_loading = false
            }
        }

        onBeforeMount(loadCompany)

        const onSubmit = async () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload: DeliveryCompanyPayload = {
                    name: state.name as string,
                    shortName: state.shortName,
                    businessModelId: state.businessModelId as number,
                    bin: state.bin as string,
                    contactPhone: state.contactPhone as string,
                    email: state.email as string,
                    kbe: Number(state.kbe as string),
                    legalAddress: state.legalAddress as string,
                    actualAddress: state.actualAddress as string,
                    bank: state.bank as string,
                    bik: state.bik as string,
                    iik: state.iik as string,
                    vatCertificateNumber: state.vatCertificateNumber as string,
                    vatSeries: state.vatSeries as string,
                    vatCertificateDate: state.vatCertificateDate as string,
                    countryId: state.countryId,
                    typeId: state.typeId,
                }

                await deliveryCompanies.updateCompany(company_id, payload)

                notifications.notify({ title: 'Компания сохранена', type: 'success' })
                router.push('/companies/spark-kz')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении компании произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            COMPANIES_TYPES,
            BUSINESS_MODELS,
            COUNTRIES,
            state,
            state_form$,
            onSubmit,
        }
    },
})
